import { useEffect, useId, useState } from 'react'
import { CircleAlert, RefreshCw } from 'lucide-react'
import { Mascot } from '#/components/layout/mascot'
import { Button } from '#/components/ui/button'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'

/** Small, CSS-only activity mark. The adjacent status text carries its meaning. */
export function LoadingMark({ className }: { className?: string }) {
  return (
    <span aria-hidden="true" className={cn('loading-mark', className)}>
      <i />
      <i />
      <i />
    </span>
  )
}

export function LoadingIndicator({
  label,
  className,
}: {
  label?: string
  className?: string
}) {
  return (
    <span
      role="status"
      aria-live="polite"
      className={cn(
        'inline-flex items-center gap-2 text-sm text-muted-foreground',
        className,
      )}
    >
      <LoadingMark />
      <span>{label ?? m.common_loading()}</span>
    </span>
  )
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('loading-skeleton block rounded-md bg-muted', className)}
    />
  )
}

/** Seconds since this view began waiting, never an estimate of completion. */
export function useWaitTime(active = true) {
  const [seconds, setSeconds] = useState(0)
  useEffect(() => {
    setSeconds(0)
    if (!active) return
    const start = Date.now()
    const timer = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - start) / 1000)),
      1000,
    )
    return () => window.clearInterval(timer)
  }, [active])
  return seconds
}

type Layout = 'cards' | 'list' | 'board' | 'document'

/** Reserve the content's shape; leave navigation and unaffected controls available. */
export function LoadingState({
  label,
  layout = 'list',
  compact = false,
  className,
}: {
  label?: string
  layout?: Layout
  compact?: boolean
  className?: string
}) {
  const seconds = useWaitTime()
  const text = label ?? m.loading_data()
  return (
    <div
      className={cn(
        'loading-state w-full min-w-0 space-y-5 p-5 sm:p-6',
        !compact && 'mx-auto max-w-5xl',
        className,
      )}
      data-loading-layout={layout}
    >
      <div
        role="status"
        aria-live="polite"
        className="flex min-w-0 items-center gap-3"
      >
        {!compact && <Mascot size={48} eager />}
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-medium">
            <LoadingMark />
            <span>{text}</span>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            {seconds >= 12 ? m.loading_slow_read() : m.loading_ready_hint()}
          </p>
        </div>
      </div>
      <div
        aria-hidden="true"
        className={cn(
          'gap-3',
          layout === 'cards'
            ? 'grid sm:grid-cols-3'
            : layout === 'board'
              ? 'grid grid-cols-1 sm:grid-cols-3'
              : 'grid',
        )}
      >
        {layout === 'document' ? (
          <div className="space-y-4 rounded-xl border bg-card p-5">
            <Skeleton className="mb-6 h-6 w-2/5" />
            {Array.from({ length: compact ? 5 : 10 }, (_, i) => (
              <Skeleton
                key={i}
                className={cn('h-3', i % 4 === 3 ? 'w-2/3' : 'w-full')}
              />
            ))}
          </div>
        ) : (
          Array.from({ length: 3 }, (_, i) => (
            <div
              key={i}
              className={cn(
                'space-y-3 rounded-xl border bg-card p-4',
                layout === 'board' && i > 0 && 'hidden sm:block',
              )}
            >
              <div className="flex items-center gap-3">
                <Skeleton className="size-8 shrink-0" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="h-3 w-5/6" />
              <Skeleton className="h-3 w-2/3" />
              {layout === 'board' && (
                <>
                  <Skeleton className="mt-4 h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

/** Long operations: observable status plus optional, user-controlled reading. */
export function WaitStatus({
  active = true,
  label,
  description,
  tips = false,
  className,
  progress,
}: {
  active?: boolean
  label: string
  description?: string
  tips?: boolean
  className?: string
  progress?: { value: number; max: number }
}) {
  const seconds = useWaitTime(active)
  const statusId = useId()
  const known =
    progress && progress.max > 0
      ? {
          max: progress.max,
          value: Math.max(0, Math.min(progress.value, progress.max)),
        }
      : null
  if (!active) return null
  return (
    <div
      className={cn(
        'loading-wait space-y-3 rounded-xl border bg-muted/25 p-4',
        className,
      )}
      data-testid="wait-status"
    >
      <div role="status" aria-live="polite" className="flex items-start gap-3">
        <LoadingMark className="mt-1" />
        <div className="min-w-0 flex-1">
          <p id={statusId} className="text-sm font-medium">
            {label}
          </p>
          {description && (
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
          {seconds >= 20 && (
            <p className="mt-2 text-xs text-muted-foreground">
              {m.loading_long_wait()}
            </p>
          )}
        </div>
      </div>
      <div
        role="progressbar"
        aria-labelledby={statusId}
        aria-valuenow={known?.value}
        aria-valuemin={known ? 0 : undefined}
        aria-valuemax={known?.max}
        className="loading-track h-1 overflow-hidden rounded-full bg-muted"
      >
        {known ? (
          <div
            className="h-full rounded-full bg-status-done transition-[width]"
            style={{ width: `${(known.value / known.max) * 100}%` }}
          />
        ) : (
          <span className="loading-track-bar block h-full w-1/3 rounded-full bg-primary" />
        )}
      </div>
      {seconds >= 5 && (
        <p
          className="text-xs tabular-nums text-muted-foreground"
          aria-live="off"
        >
          {m.loading_elapsed({ seconds })}
        </p>
      )}
      {tips && (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer rounded-sm py-1 font-medium text-foreground focus-visible:outline-2 focus-visible:outline-ring">
            {m.loading_tip_title()}
          </summary>
          <p className="pt-2 leading-relaxed">{m.loading_tip_body()}</p>
        </details>
      )}
    </div>
  )
}

export function QueryError({
  onRetry,
  retrying = false,
  className,
}: {
  onRetry?: () => unknown
  retrying?: boolean
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        'm-4 space-y-3 rounded-xl border border-destructive/25 bg-card p-5',
        className,
      )}
    >
      <p className="flex items-center gap-2 text-sm font-medium">
        <CircleAlert className="size-4 shrink-0 text-destructive" />
        {m.loading_error_title()}
      </p>
      <p className="text-sm text-muted-foreground">{m.loading_error_hint()}</p>
      {onRetry && (
        <Button
          variant="outline"
          size="sm"
          loading={retrying}
          loadingLabel={m.common_loading()}
          onClick={() => void onRetry()}
        >
          <RefreshCw className="size-4" />
          {m.brand_retry()}
        </Button>
      )}
    </div>
  )
}
